import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { MdPassword } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { DocumentProps } from '../..';
import { useStateValue } from '../../../../providers/StateProvider';
import { requestCodeSign, sendCodeSignToValidate, checkValidToken, CheckValidTokenResult, resendSignatureEmail } from '../../../../services/signature';
import { actionTypes } from '../../../../store/reducer';
import { ButtonCancel, ButtonSave, CardFooterCustom, CardIcon } from '../../styles';
import { BodyModal, ButtonSendCode, DescriptionModal, InputCode, MessageError, MessageSuccess, SendCodeMessage, TitleModal } from './styles';
import Loading from '../../../../Components/Loading';
import formatTimeRemaining from '../../../../utils/formatTimeRemaining';
import { formatTimeHourMinMS } from '../../../../utils/formatDateMS';
import { maskEmail } from '../../../../utils/maskEmail';
import { cleanupBodyScrollLock } from '../../../../utils/modalCleanup';

type Props = {
    show: boolean;
    setShow: (show: boolean) => void;
    documents: DocumentProps;
};

interface RequestCode {
    message: string;
    state: string;
}

const ModalCodeValidate = ({ show, setShow, documents }: Props): JSX.Element => {
    const history = useHistory();
    const [code, setCode] = useState<string>('');
    const handleClose = () => {
        setShow(false);
        cleanupBodyScrollLock();
    };
    const [errorInput, setErrorInput] = useState<boolean>(false);
    const [successInput, setSuccessInput] = useState<boolean>(false);
    const [, dispatch] = useStateValue();
    const [requestCode, setRequestCode] = useState<RequestCode>();
    const [email, setEmail] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [, setCodingValidation] = useState();

    const [tokenStatus, setTokenStatus] = useState<CheckValidTokenResult | null>(null);
    const [checkingToken, setCheckingToken] = useState<boolean>(true);
    const [invalidCodeError, setInvalidCodeError] = useState<boolean>(false);
    const [resentMessage, setResentMessage] = useState<boolean>(false);

    const handleAssignCode = (e) => {
        const value: string = e.target.value.replace(/\D/g, '');
        setCode(value);
        setInvalidCodeError(false);
        if (value.length !== 6) {
            setErrorInput(true);
            setSuccessInput(false);
        } else {
            setErrorInput(false);
            setSuccessInput(true);
        }
    }

    const handleSave = () => {
        history.goBack();
        dispatch({
            type: actionTypes.SET_FLASH_MESSAGE,
            flashMessage: {
                show: true,
                type: 'success-alt',
                title: 'Sucesso',
                message: 'Documento criado!',
            },
        });
    }

    const validateCode = async () => {
        setLoading(true);
        setInvalidCodeError(false);
        try {
            const token: string | null = localStorage.getItem('gov_access_token');

            const stateToUse = requestCode?.state || tokenStatus?.state;

            if (!stateToUse) {
                console.error('Nenhum state disponível para validação');
                return;
            }

            const { data } = await sendCodeSignToValidate({
                code,
                state: stateToUse,
                identificador: documents.id,
                tipo: 'Documento',
                internal_state: stateToUse,
                client_id: (window as any)._env_.REACT_APP_FORMS_CLIENT_ID,
                token
            });
            setCodingValidation(data);
            handleSave();
        } catch (error) {
            console.log(error);
            if (error?.response?.status === 404 ||
                error?.response?.data?.message?.includes('inexistente') ||
                error?.response?.data?.message?.includes('inválido') ||
                error?.response?.data?.message?.includes('utilizado')) {
                setInvalidCodeError(true);
                setErrorInput(true);
                setSuccessInput(false);
            }
        } finally {
            setLoading(false);
        }
    }

    const handleRequestCode = async () => {
        setInvalidCodeError(false);
        const dataStorage = localStorage.getItem('gov_user_data');
        if (!dataStorage) {
            return;
        }
        const userData = JSON.parse(dataStorage);

        const token: string | null = localStorage.getItem('gov_access_token');

        setResentMessage(false);

        const tokenValidation = await checkValidToken(userData.cpf, userData.contato.email, token);
        setTokenStatus(tokenValidation);

        if (!tokenValidation.hasValidToken) {
            const { data } = await requestCodeSign(userData.cpf, userData.contato.email, token);
            setRequestCode(data);
        }
    }

    const handleRequestCodeResend = async () => {
        setInvalidCodeError(false);
        const dataStorage = localStorage.getItem("gov_user_data");
        const userData = JSON.parse(dataStorage);
        const token: string | null = localStorage.getItem('gov_access_token');

        const tokenValidation = await checkValidToken(userData.cpf, userData.contato.email, token);
        setTokenStatus(tokenValidation);

        if (tokenValidation.hasValidToken) {
            await resendSignatureEmail(userData.cpf, userData.contato.email, token);
            setResentMessage(true);
            return;
        }

        setResentMessage(false);
    }

    useEffect(() => {
        const dataStorage = localStorage.getItem('gov_user_data');
        if (dataStorage) {
            try {
                const userData = JSON.parse(dataStorage);
                setEmail(userData?.contato?.email);
            } catch (err) {
                console.error(err);
            }
        }
    }, []);

    useEffect(() => {
        if (!show) {
            cleanupBodyScrollLock();
            return;
        }

        const checkTokenAndRequestCode = async () => {
            if (!documents) {
                return;
            }
            setCheckingToken(true);
            setResentMessage(false);
            try {
                await handleRequestCode();
            } catch (err) {
                console.error(err);
                setTokenStatus({ hasValidToken: false, message: 'Erro ao verificar token de assinatura.' });
            } finally {
                setCheckingToken(false);
            }
        };

        checkTokenAndRequestCode();
    }, [documents, show]);

    return (
        <>
            <Modal
                size='lg'
                show={show}
                centered
                onHide={handleClose}
                onExited={cleanupBodyScrollLock}
                backdrop
                keyboard
            >
                <Modal.Body>
                    <BodyModal>
                        <CardIcon>
                            <MdPassword />
                        </CardIcon>
                        <TitleModal>Assinatura eletrônica</TitleModal>
                        {checkingToken ? (
                            <Loading />
                        ) : (
                            <>
                                {tokenStatus && tokenStatus.hasValidToken ? (
                                    <DescriptionModal>
                                        {resentMessage ? (
                                            <>Um e-mail com seu token atual foi enviado para <strong>{maskEmail(email ?? '')}</strong>. Seu token tem validade de <strong>
                                                {tokenStatus.expiresAt && formatTimeRemaining(tokenStatus.expiresAt)}
                                            </strong></>
                                        ) : (
                                            <>Você possui token válido neste momento. Use o token atual enviado anteriormente para o seu e-mail <strong>{maskEmail(email ?? '')}</strong>. Seu token tem validade de <strong>
                                                {tokenStatus.expiresAt && formatTimeRemaining(tokenStatus.expiresAt)}
                                            </strong></>
                                        )}
                                    </DescriptionModal>
                                ) : (
                                    <DescriptionModal>
                                        {tokenStatus?.expiresAt ? (
                                            <>Seu token expirou às <strong>{formatTimeHourMinMS(tokenStatus.expiresAt)}</strong>. Um novo token válido foi enviado para o seu e-mail: <strong>{maskEmail(email ?? '')}</strong></>
                                        ) : (
                                            <>Seu token expirou. Um novo token válido foi enviado para o seu e-mail: <strong>{maskEmail(email ?? '')}</strong></>
                                        )}
                                    </DescriptionModal>
                                )}

                                {loading ? (
                                    <Loading />
                                ) : (
                                    <InputCode placeholder='000000' maxLength={6} value={code}
                                        onChange={handleAssignCode}
                                        error={errorInput}
                                        success={successInput}
                                    />
                                )}
                                {errorInput && !invalidCodeError && <MessageError>Código expirado ou inexistente</MessageError>}
                                {invalidCodeError && <MessageError>Código expirado ou inexistente</MessageError>}

                                {successInput && <MessageSuccess>Verificado</MessageSuccess>}
                                <SendCodeMessage>Não recebeu o código? <ButtonSendCode onClick={handleRequestCodeResend}>Reenviar</ButtonSendCode></SendCodeMessage>
                            </>
                        )}
                    </BodyModal>
                </Modal.Body>
                <CardFooterCustom>
                    <ButtonCancel onClick={handleClose}>
                        Cancelar
                    </ButtonCancel>
                    <ButtonSave
                        onClick={validateCode}
                        disabled={!successInput || checkingToken || (!requestCode?.state && !tokenStatus?.state)}
                    >
                        Finalizar
                    </ButtonSave>
                </CardFooterCustom>
            </Modal>
        </>
    );
};

export default ModalCodeValidate;
